import gql from 'graphql-tag';
import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import fetch from 'unfetch';
import { GRAPHQL_SERVER } from '../servers';
import { handleRequestErrors } from '../errorHandlingService';
import packageJson from '../../../package.json';
import {
  forceRefreshCognitoSession,
  getCognitoAccessToken,
} from '@services/cognitoUserService';

const appVersion = packageJson.version;

const httpLink = createHttpLink({
  uri: GRAPHQL_SERVER,
  fetch,
});

const isUnauthorizedError = (error) => {
  const statusCode =
    error?.status ||
    error?.statusCode ||
    error?.response?.status ||
    error?.response?.statusCode ||
    error?.networkError?.statusCode;
  return statusCode === 401;
};

const parseResponseBody = async (res) => {
  const contentType = res?.headers?.get?.('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch (e) {
      return null;
    }
  }
  try {
    return await res.text();
  } catch (e) {
    return null;
  }
};

const buildAuthHeaders = async ({ authTokenOverride = null } = {}) => {
  const override = String(authTokenOverride || '').trim();
  const token = override || (await getCognitoAccessToken());
  const headers = {
    'X-Novotalk-Source': 'novotalk_web_and_desktop',
    'X-Novotalk-Version': appVersion,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const toHandledError = (statusCode, body) => {
  const err = new Error('Request failed');
  err.status = statusCode;
  err.statusCode = statusCode;
  err.networkError = { statusCode };
  err.response = { status: statusCode, statusCode, body };
  return err;
};

const fetchWithAuth = async (
  url,
  options = {},
  { retryOn401 = true, authTokenOverride = null } = {},
) => {
  const override = String(authTokenOverride || '').trim();
  const effectiveRetryOn401 = override ? false : retryOn401;
  const headers = {
    ...(options.headers || {}),
    ...(await buildAuthHeaders({ authTokenOverride: override })),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && effectiveRetryOn401) {
    await forceRefreshCognitoSession();
    return fetchWithAuth(url, options, { retryOn401: false, authTokenOverride: override });
  }

  const body = await parseResponseBody(res);
  if (!res.ok) {
    const err = toHandledError(res.status, body);
    try {
      handleRequestErrors(err);
    } catch (handledError) {
      if (handledError && typeof handledError === 'object') {
        handledError.status = err.status;
        handledError.statusCode = err.statusCode;
        handledError.networkError = err.networkError;
        handledError.response = err.response;
      }
      throw handledError;
    }
    throw err;
  }

  return { ok: true, status: res.status, body };
};

const buildFormUrlEncodedBody = (params) => {
  if (!params || typeof params !== 'object') return '';

  const searchParams = new URLSearchParams();

  const appendValue = (key, value) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => appendValue(`${key}[]`, item));
      return;
    }

    if (value instanceof Date) {
      searchParams.append(key, value.toISOString());
      return;
    }

    if (typeof value === 'object') {
      Object.keys(value).forEach((childKey) => {
        appendValue(`${key}[${childKey}]`, value[childKey]);
      });
      return;
    }

    searchParams.append(key, String(value));
  };

  Object.keys(params).forEach((key) => appendValue(key, params[key]));
  return searchParams.toString();
};

// Token/session helpers are centralized in cognitoUserService.

const authLink = setContext(async (_, context) => {
  const headers = {
    ...(context?.headers || {}),
    ...(await buildAuthHeaders()),
  };

  return { headers };
});

export const graphClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

export const graphQuery = (query, wrapWithGQL = false, variables = {}, signal) => {
  const execute = () =>
    graphClient.query({
      query: wrapWithGQL ? gql(query) : query,
      fetchPolicy: 'no-cache',
      variables,
      context: signal ? { fetchOptions: { signal } } : undefined,
    });

  return execute()
    .then((result) => {
      if (result.data.error) {
        throw new Error(result.data.error);
      }
      return result;
    })
    .catch(async (e) => {
      if (e?.name === 'AbortError' || signal?.aborted) {
        throw e;
      }

      if (isUnauthorizedError(e)) {
        await forceRefreshCognitoSession();
        try {
          const retried = await execute();
          if (retried.data?.error) {
            throw new Error(retried.data.error);
          }
          return retried;
        } catch (e2) {
          console.log(e2);
          handleRequestErrors(e2);
          return undefined;
        }
      }

      console.log(e);
      handleRequestErrors(e);
      return undefined;
    });
};
export const graphMutation = (
  mutation,
  variables = {},
  wrapWithGQL = false,
  returnGraphQLErrors = false,
) =>
  (async () => {
    const execute = () =>
      graphClient.mutate({
        mutation: wrapWithGQL ? gql(mutation) : mutation,
        variables,
        errorPolicy: returnGraphQLErrors ? 'all' : 'none',
      });

    try {
      return await execute();
    } catch (e) {
      if (isUnauthorizedError(e)) {
        await forceRefreshCognitoSession();
        try {
          return await execute();
        } catch (e2) {
          console.log(e2);
          handleRequestErrors(e2);
          return undefined;
        }
      }
      console.log(e);
      handleRequestErrors(e);
      return undefined;
    }
  })();

export const getRequest = (server, endpoint, requestOptions = {}) =>
  (() => {
    const p = fetchWithAuth(
      `${server}${endpoint}`,
      { method: 'GET' },
      requestOptions,
    ).then(
      (r) => r.body,
    );
    // Avoid unhandled rejections when callers don't await.
    p.catch(() => undefined);
    return p;
  })();

export const postFormRequest = (
  server,
  endpoint,
  params = null,
  file = null,
  requestOptions = {},
) => {
  const url = `${server}${endpoint}`;
  if (!file) {
    // No file: preserve previous behavior (JSON body)
    const p = fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: params ? JSON.stringify(params) : null,
    }, requestOptions);
    p.catch(() => undefined);
    return p;
  }

  const formData = new FormData();
  if (params) {
    Object.keys(params).forEach((key) => {
      const value = params[key];
      formData.append(
        key,
        typeof value === 'string' ? value : JSON.stringify(value),
      );
    });
  }
  formData.append('file', file);

  const p = fetchWithAuth(url, {
    method: 'POST',
    body: formData,
  }, requestOptions);
  p.catch(() => undefined);
  return p;
};

export const postRequest = (server, endpoint, params = null, type = 'form', requestOptions = {}) => {
  const url = `${server}${endpoint}`;
  if (type === 'json') {
    const p = fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: params ? JSON.stringify(params) : null,
    }, requestOptions);
    p.catch(() => undefined);
    return p;
  }

  // Default: form encoded
  const body = params ? buildFormUrlEncodedBody(params) : '';
  const p = fetchWithAuth(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body,
  }, requestOptions);
  p.catch(() => undefined);
  return p;
};
